/**
 * The layout plan: which side the road takes and which the ravines, where each linear
 * obstacle is crossed, where the compound sits, its gates, the pickup and the start.
 * Nothing is built here - later steps read the plan and lay things down.
 */

import type { Pcg32 } from '../sim/rng';
import type { Rect } from '../sim/types';
import type { ArchetypeSpec } from './archetypes/types';
import type { BiomeKit } from './biomes';
import { BANDS, COMPOUND, LINES, MAP, type Range } from './tuning';
import type { Band, Gap, Gate, LineKind, LinePlan, Plan } from './types';

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));
const snap = (v: number): number => Math.round(v * 2) / 2;

function weighted<T>(rng: Pcg32, items: ReadonlyArray<readonly [T, number]>, fallback: T): T {
  let total = 0;
  for (const [, w] of items) total += w;
  let roll = rng.float() * total;
  for (const [item, w] of items) {
    roll -= w;
    if (roll < 0) return item;
  }
  return fallback;
}

function planLines(rng: Pcg32, bands: Band[], kit: BiomeKit, sizeX: number, roadEast: boolean): LinePlan[] {
  const lane = (r: Range, east: boolean): Range =>
    (east ? [r[0] * sizeX, r[1] * sizeX] : [(1 - r[1]) * sizeX, (1 - r[0]) * sizeX]);
  const roadLane = lane(LINES.roadLane, roadEast);
  const quietLane = lane(LINES.quietLane, roadEast);
  const boundaries = bands.filter((b) => b.kind === 'drop' || b.kind === 'approach').map((b) => b.z0);

  const lines: LinePlan[] = [];
  let roadX = rng.range(roadLane[0], roadLane[1]);
  let quietX = rng.range(quietLane[0], quietLane[1]);
  let previous: LineKind | null = null;
  const light = kit.lines.filter(([k]) => k !== 'wall');
  boundaries.forEach((z, i) => {
    roadX = clamp(roadX + rng.range(-LINES.roadDrift, LINES.roadDrift), roadLane[0], roadLane[1]);
    quietX = clamp(quietX + rng.range(-LINES.quietDrift, LINES.quietDrift), quietLane[0], quietLane[1]);
    let kind: LineKind;
    if (i === boundaries.length - 1) kind = 'wire'; // the belt's wire line
    // The line on the drop band's edge is a light one: usually a trench, never a wall.
    else if (i === 0) kind = rng.chance(0.7) ? 'trench' : weighted(rng, light, 'trench');
    else kind = weighted(rng, kit.lines.filter(([k]) => k !== previous), 'trench');
    previous = kind;

    const midX = snap((roadX + quietX) / 2 + rng.range(-4, 4));
    const road: Gap = { x: snap(roadX), width: LINES.roadGap };
    const quiet: Gap = { x: snap(quietX), width: LINES.quietGap };
    const mid: Gap = { x: midX, width: LINES.extraGap };
    // A trench line is joined by the ravines at the quiet crossing, so its gaps are the other two.
    const gaps = kind === 'trench' ? [road, mid] : rng.chance(0.5) ? [road, quiet, mid] : [road, quiet];
    lines.push({ z, kind, roadX: road.x, quietX: quiet.x, midX, gaps });
  });
  return lines;
}

function planCompound(
  rng: Pcg32, spec: ArchetypeSpec, goal: Band, sizeX: number, roadEast: boolean, threat: number,
): Rect {
  if (spec.layout === 'border') return { x0: MAP.edge, z0: 16, x1: sizeX - MAP.edge, z1: goal.z1 - 4 };
  const fixed = spec.sizeFor?.(threat);
  const w = fixed ? fixed.w : Math.round(rng.range(spec.size.w[0], spec.size.w[1]) / 2) * 2;
  const wantD = fixed ? fixed.d : Math.round(rng.range(spec.size.d[0], spec.size.d[1]) / 2) * 2;
  const room = Math.min(goal.z1, BANDS.goal[1]) - COMPOUND.southMargin - COMPOUND.northMargin[0];
  const d = Math.min(wantD, room);
  const north = Math.round(rng.range(COMPOUND.northMargin[0], COMPOUND.northMargin[1]));
  const z0 = Math.min(north, goal.z1 - COMPOUND.southMargin - d);
  const margin = Math.round(rng.range(COMPOUND.sideMargin[0], COMPOUND.sideMargin[1]));
  const x0 = roadEast ? sizeX - margin - w : margin;
  return { x0, z0, x1: x0 + w, z1: z0 + d };
}

export function makePlan(
  rng: Pcg32, bands: Band[], spec: ArchetypeSpec, kit: BiomeKit, sizeX: number, threat: number,
): Plan {
  const roadEast = rng.chance(0.5);
  const lines = planLines(rng.fork('lines'), bands, kit, sizeX, roadEast);
  const goal = bands[bands.length - 1] ?? { kind: 'goal', z0: 0, z1: BANDS.goal[0] };
  const drop = bands[0] ?? { kind: 'drop', z0: 0, z1: 0 };
  const compound = planCompound(rng.fork('compound'), spec, goal, sizeX, roadEast, threat);
  const midZ = (compound.z0 + compound.z1) / 2;
  const lastRoadX = lines[lines.length - 1]?.roadX ?? sizeX / 2;
  const roadEndX = snap(clamp(lastRoadX, compound.x0 + 7, compound.x1 - 7));

  const gates: Gate[] = [];
  if (spec.perimeter !== 'none' && !spec.sealed && spec.layout === 'compound') {
    gates.push({ side: 'south', x: roadEndX, z: compound.z1, width: COMPOUND.gateWidth });
    const side = roadEast ? 'west' : 'east';
    const gateZ = snap(clamp(midZ + rng.range(-4, 4), compound.z0 + 6, compound.z1 - 6));
    gates.push({ side, x: roadEast ? compound.x0 : compound.x1, z: gateZ, width: COMPOUND.sideGateWidth });
  }

  let pickup = { x: snap(roadEast ? sizeX * 0.3 : sizeX * 0.7), z: 8.5 };
  if (spec.layout === 'compound') {
    const room = roadEast ? compound.x0 : sizeX - compound.x1;
    const dist = clamp(rng.range(COMPOUND.pickupDistance[0], COMPOUND.pickupDistance[1]), 8.5, Math.max(8.5, room - 5));
    pickup = {
      x: Math.floor(roadEast ? compound.x0 - dist : compound.x1 + dist) + 0.5,
      z: Math.floor(midZ + rng.range(spec.pickupDz?.[0] ?? -5, spec.pickupDz?.[1] ?? 5)) + 0.5,
    };
  }
  const player = {
    x: Math.floor(sizeX / 2 + rng.range(-16, 16)) + 0.5,
    z: Math.floor(drop.z1 - rng.range(10, 22)) + 0.5,
  };
  // The Inside Man's gate is cut later (`insideMan.ts`): it depends on the intel, and the plan is the
  // shape of the ground, which must be the same map with the card as without it.
  return {
    roadEast, lines, compound, gates, insideGate: null, pickup, player,
    roadEnd: { x: roadEndX, z: compound.z1 },
  };
}
